/**
 * Content-Design-authored copy for the two unsuccessful section-save
 * outcomes (TG-8). ADR-20260826-94qx requires DISTINCT wording for Failed
 * versus Indeterminate, and requires that both retain every answer on
 * screen and offer the SAME confirm control as the way forward — so neither
 * message below names or implies a separate "retry" action, only the
 * screen's own existing action control.
 *
 * m1-portal-ux-fixes (Fix C, product owner approved 2026-10-28): H2's own
 * handoff supplies no save-failure copy at all (CONVENTIONS.md is silent on
 * it), and the previous wording here ("...select Confirm to try again")
 * named a control — "Confirm" — that does not exist on every screen this
 * content serves (confirm-details' and the exposure sections' own primary
 * control is "Continue"; declaration's is "Submit renewal"; the assign/
 * reassign dialogs have their own confirm labels). `buildSaveOutcomeContent`
 * is therefore a function of the screen's own primary action label, not a
 * static object — every caller supplies ITS OWN button's visible text, never
 * a hardcoded "Confirm". No em dashes (house style).
 *
 * `tone` names the H2 square/4px-rule panel tone each outcome renders with
 * (`PortalPanelTone` from `../../design-system`) — both Failed and
 * Indeterminate render `"error"`: H2 defines no "warning" tone
 * (CONVENTIONS.md, DESIGN-SYSTEM-DEPARTURES.md), so an Indeterminate
 * outcome — previously the design system `Alert`'s own `severity="warning"`
 * — now renders identically to Failed. This is a plain string literal, not
 * an import of `PortalPanelTone` itself: this module authors content and
 * must not reach into the design-system's own type surface to do it.
 */
export interface SaveOutcomeContentEntry {
  readonly tone: "error";
  readonly title: string;
  readonly message: string;
}

export type SaveOutcomeContent = Record<"Failed" | "Indeterminate", SaveOutcomeContentEntry>;

/**
 * Builds this pair's content for one screen, named by `actionLabel` — the
 * exact visible text of that screen's own primary action control (e.g.
 * "Continue", "Submit renewal", or a dialog's own confirm label). Called
 * once per render by each caller; the two entries are otherwise identical
 * for every caller, so there is exactly one copy of each sentence, not one
 * per screen.
 */
export function buildSaveOutcomeContent(actionLabel: string): SaveOutcomeContent {
  return {
    Failed: {
      tone: "error",
      title: "We could not save your answers",
      message: `Something went wrong on our side. Your answers are still on this page. Select ${actionLabel} to try again.`,
    },
    Indeterminate: {
      tone: "error",
      title: "We could not confirm your answers saved",
      message: `We lost the connection before we could check. Your answers are still on this page. Select ${actionLabel} to try again.`,
    },
  };
}
